import {useMemo} from "react";
import {Collapse, Loader} from "@mantine/core";
import {useDisclosure, useMediaQuery} from "@mantine/hooks";
import slugify from "@sindresorhus/slugify";
import {useContentParams} from "./router";
import {t} from "i18next";
import {dibAppApi} from "../../dibAppApi/dibAppApi";
import {useReportAccess} from "./AccessContext";
import {ResourceError} from "./ResourceError";
import {StatusMessage} from "./StatusMessage";
import type {
    ChecklistFilterGroup,
    RawChecklistResponse,
    RenderableDocument,
    Status,
} from "./types";
import {ResourceType} from "./types";
import {checklistFrom} from "./checklistTransform";
import {renderNode} from "./renderNode";
import layoutClasses from "./ContentLayout.module.css";
import classes from "./checklist/checklist.module.css";

import {
    ChecklistStructureProvider,
    ChecklistInteractionProvider,
    type ChecklistStructureValue,
    type ChecklistInteractionValue,
} from "./checklist/ChecklistDataContext";
import {
    useChecklistState,
    ChecklistEventTypes,
} from "./checklist/hooks/useChecklistState";
import {useChecklistModals} from "./checklist/hooks/useChecklistModals";
import {useChecklistScrollSpy} from "./checklist/hooks/useChecklistScrollSpy";
import {
    getRelatedDocuments,
    getRelatedDocumentsOrder,
    getChecklistFilterGroups,
} from "./checklist/utils";
import {useViewFiltering} from "./checklist/hooks/useViewFiltering";
import {useChecklistVisibility} from "./checklist/hooks/useChecklistVisibility";
import {useChecklistActions} from "./checklist/hooks/useChecklistActions";
import {useCSSHighlights} from "../../useCssHighlights";

import ChecklistToc from "./checklist/ChecklistToc";
import {ChecklistTree} from "./checklist/ChecklistTree";
import {ChecklistConfig} from "./checklist/ChecklistConfig";
import {ChecklistControls} from "./checklist/ChecklistControls";
import {ChecklistMobileToolbar} from "./checklist/ChecklistMobileToolbar";
import {ChecklistMobileControls} from "./checklist/ChecklistMobileControls";
import {GlobalFilterSettings} from "./checklist/GlobalFilterSettings";
import {DocumentSelectorField} from "./checklist/DocumentSelectorField";
import {ChecklistModals} from "./checklist/ChecklistModals";
import {DocumentsList} from "./checklist/DocumentsList";
import {GenerateButtons} from "./generate/GenerateButtons";
import type {CollectionMeta, ExportDocument, TopicMeta} from "./generate/types";
import {TopBarSlot} from "../chat/TopBarContext";
import {ChecklistViewTabs} from "./checklist/ChecklistViewTabs";
import {CommentProvider} from "./checklist/comments/CommentContext";
import {CommentPanel} from "./checklist/comments/CommentPanel";
import {
    DocgenChecklistView,
    DOCUMENT_GENERATOR_PLACEHOLDER_ID,
} from "./docgen/DocgenChecklistView";
import {ResizeHandle} from "./resizable/ResizeHandle";
import {useResizable} from "./resizable/useResizable";
import {useContentTitle} from "./useContentTitle";

// ---------------------------------------------------------------------------
// Outer component: data fetching + loading/error handling
// ---------------------------------------------------------------------------

export const ChecklistResource = ({
    resourceId,
    collectionId,
    dokumentId,
    isPreview,
}: {
    resourceId: string;
    collectionId?: string;
    dokumentId?: string;
    isPreview?: boolean;
}) => {
    const {
        data: rawData,
        isLoading,
        isError,
        error,
    } = dibAppApi.endpoints.getChecklist.useQuery({
        topicId: resourceId,
        isPreview,
    });

    // Set in the outer component (above the early returns) so the checklist
    // loading window also reads "KAI" before ChecklistView mounts.
    useContentTitle(rawData?.name);

    useReportAccess(resourceId, rawData?.access);

    if (isLoading) {
        return (
            <div className={layoutClasses.statusMessage}>
                <Loader color="gneis.5" size="md" />
            </div>
        );
    }

    if (isError) {
        return <ResourceError error={error} />;
    }

    if (!rawData) return null;

    return (
        <ChecklistView
            // Remount the checklist subtree when the collection identity changes
            // (open / switch / delete) so local state never lingers stale — e.g.
            // after deleting the active filling the view renders fresh.
            key={collectionId ?? "no-collection"}
            rawData={rawData}
            collectionId={collectionId}
            dokumentId={dokumentId}
            isPreview={isPreview ?? false}
        />
    );
};

// ---------------------------------------------------------------------------
// Inner component: the full orchestrator
// ---------------------------------------------------------------------------

const ChecklistView = ({
    rawData,
    collectionId,
    dokumentId,
    isPreview,
}: {
    rawData: RawChecklistResponse;
    collectionId?: string;
    dokumentId?: string;
    isPreview: boolean;
}) => {
    const checklistData = useMemo(() => checklistFrom(rawData), [rawData]);

    // -----------------------------------------------------------------------
    // Step 2: Collection data queries
    // -----------------------------------------------------------------------

    const {
        data: collectionData,
        isFetching: isCollectionFetching,
        isError: isCollectionError,
    } = dibAppApi.endpoints.getChecklistCollectionData.useQuery(
        {
            topicId: checklistData.metadata.topicId,
            resourceId: checklistData.metadata.id,
            collectionId: collectionId ?? "",
        },
        {skip: !collectionId}
    );

    const {
        data: defaultCollectionData,
        isFetching: isDefaultCollectionFetching,
    } = dibAppApi.endpoints.getChecklistCollectionData.useQuery(
        {
            topicId: checklistData.metadata.topicId,
            resourceId: checklistData.metadata.id,
            collectionId: checklistData.defaultCollectionId ?? "",
        },
        {
            skip: !checklistData.defaultCollectionId || !!collectionId,
        }
    );

    // -----------------------------------------------------------------------
    // Step 3: Mutation hooks
    // -----------------------------------------------------------------------

    const [patchDimensions, {isLoading: isPatchDimensionsLoading}] =
        dibAppApi.endpoints.patchDimensions.useMutation();
    const [patchCollectionName, {isLoading: isPatchCollectionNameLoading}] =
        dibAppApi.endpoints.patchCollectionName.useMutation();
    const [patchStatusForCheckItems] =
        dibAppApi.endpoints.patchStatusForCheckItems.useMutation();
    const [patchDocumentSelected] =
        dibAppApi.endpoints.patchDocumentSelected.useMutation();
    const [getChecklistPartElements] =
        dibAppApi.endpoints.getChecklistPartElements.useLazyQuery();

    // -----------------------------------------------------------------------
    // Step 4: Comments query (seed RTK cache)
    // -----------------------------------------------------------------------

    const {data: commentsData} = dibAppApi.endpoints.getComments.useQuery(
        {collectionId: collectionId ?? ""},
        {skip: !collectionId}
    );

    // -----------------------------------------------------------------------
    // Step 5: useChecklistState hook
    // -----------------------------------------------------------------------

    const {state, dispatch, fetchItem, fetchAll} = useChecklistState({
        elements: checklistData.elements,
        objects: checklistData.objects,
        isPreview,
        resourceId: checklistData.metadata.id,
        getChecklistPartElements: async (params) =>
            getChecklistPartElements(params).unwrap(),
    });

    // Cast via unknown: ChecklistElement.attributes is optional and
    // ChecklistObject has a different discriminator than DibObject, so
    // state.elements / state.objects aren't structurally assignable to
    // RenderableDocument. Shape is compatible at runtime.
    const topicData = useMemo(
        () =>
            ({
                elements: state.elements,
                objects: state.objects,
                elementdata: {},
                root: [],
                topicId: rawData.topicId,
                id: rawData.id,
                resourceTypeId: rawData.resourceTypeId,
                segmentId: "",
                topictypeId: rawData.topictypeId,
                name: rawData.name,
            }) as unknown as RenderableDocument,
        [state.elements, state.objects, rawData]
    );

    // -----------------------------------------------------------------------
    // Step 6: useChecklistModals hook
    // -----------------------------------------------------------------------

    const {
        isEditFillingOpen,
        isCreateFillingOpen,
        isOpenFillingOpen,
        isShareFillingOpen,
        isDuplicateFillingOpen,
        isDeleteFillingOpen,
        confirmCheckAllData,
        openEditFilling,
        openCreateFilling,
        openOpenFilling,
        openShareFilling,
        openDuplicateFilling,
        openDeleteFilling,
        openConfirmCheckAll,
        closeModal,
    } = useChecklistModals();

    // -----------------------------------------------------------------------
    // Step 7: Derived state
    // -----------------------------------------------------------------------

    const {labelData, viewFilteredToc, effectiveRootNodeId} = useViewFiltering(
        checklistData,
        collectionData,
        defaultCollectionData
    );

    const {
        hiddenIds,
        searchHitIds,
        statusMap,
        infoIdSet,
        hasOnlyInfoNodes,
        collectionProgress,
        allItemsExpanded,
    } = useChecklistVisibility({
        viewFilteredToc,
        effectiveRootNodeId,
        statusFilterSelected: state.filter.statusFilterSelected,
        searchTerm: state.searchTerm,
        elements: state.elements,
        openItems: state.openItems,
        collectionData,
        commentsData,
        checklistData,
    });

    const isDraft =
        !collectionData?.metadata ||
        collectionData.metadata.collectionId === undefined ||
        collectionData.metadata.collectionId ===
            checklistData.defaultCollectionId;

    const activeFilters: ChecklistFilterGroup[] = getChecklistFilterGroups(
        state.filter.statusFilterSelected,
        checklistData.objects ?? {}
    );

    const relatedDocuments = useMemo(
        () => getRelatedDocuments(viewFilteredToc, effectiveRootNodeId),
        [viewFilteredToc, effectiveRootNodeId]
    );
    const relatedDocumentsOrder = useMemo(
        () => getRelatedDocumentsOrder(viewFilteredToc, effectiveRootNodeId),
        [viewFilteredToc, effectiveRootNodeId]
    );

    const generateButtonProps = useMemo(() => {
        const topicMeta: TopicMeta = {
            topicId: checklistData.metadata.topicId,
            resourceId: checklistData.metadata.id,
            resourceTypeId:
                (checklistData.metadata.resourceTypeId as ResourceType) ??
                undefined,
            name: checklistData.metadata.name,
        };
        const activeCollection = collectionData ?? defaultCollectionData;
        const collectionMeta: CollectionMeta = {
            id: activeCollection?.metadata?.collectionId ?? "",
        };
        const documents: ExportDocument[] = (activeCollection?.documents ?? [])
            .filter((d) => relatedDocuments[d.topicId] !== undefined)
            .map((d) => ({
                id: d.topicId,
                name: relatedDocuments[d.topicId]?.name ?? "",
                selected: true,
                resourceTypeId: relatedDocuments[d.topicId]?.resourceTypeId as
                    ResourceType | undefined,
            }))
            .sort(
                (a, b) =>
                    (relatedDocumentsOrder[a.id] ?? 0) -
                    (relatedDocumentsOrder[b.id] ?? 0)
            );

        const labels = Object.keys(state.filter.statusFilterSelected)
            .filter((k) => k !== "status")
            .flatMap(
                (k) =>
                    state.filter.statusFilterSelected[k] as (string | Status)[]
            )
            .map(String);
        const status = state.filter.statusFilterSelected.status;

        return {topicMeta, collectionMeta, documents, labels, status};
    }, [
        checklistData.metadata,
        collectionData,
        defaultCollectionData,
        relatedDocuments,
        relatedDocumentsOrder,
        state.filter.statusFilterSelected,
    ]);

    const isGenerateFetching =
        isCollectionFetching || isDefaultCollectionFetching;

    // -----------------------------------------------------------------------
    // Step 8: Scroll spy
    // -----------------------------------------------------------------------

    const {
        containerRef,
        tocContainerRef,
        locked,
        manuallySetActiveElementId,
        observeElement,
        unobserveElement,
        scrollToCheckItem,
        scrollToActiveTocItem,
        setActiveTocItem,
    } = useChecklistScrollSpy(checklistData.tocParentMap);

    // -----------------------------------------------------------------------
    // Step 9: CSS highlights
    // -----------------------------------------------------------------------

    useCSSHighlights(state.searchTerm, "[data-searchable]", [
        state.searchTerm,
        state.fetchedAll,
        viewFilteredToc,
        state.openItems,
    ]);

    // -----------------------------------------------------------------------
    // Step 10: Actions
    // -----------------------------------------------------------------------

    const actions = useChecklistActions({
        checklistData,
        collectionData,
        viewFilteredToc,
        effectiveRootNodeId,
        labelData,
        hiddenIds,
        allItemsExpanded,
        state,
        dispatch,
        fetchItem,
        fetchAll,
        scrollSpy: {
            containerRef,
            tocContainerRef,
            locked,
            manuallySetActiveElementId,
            scrollToCheckItem,
            scrollToActiveTocItem,
            setActiveTocItem,
        },
        mutations: {
            patchDimensions: (params) => patchDimensions(params).unwrap(),
            patchCollectionName: (params) =>
                patchCollectionName(params).unwrap(),
            patchStatusForCheckItems,
            patchDocumentSelected,
        },
        modals: {
            openCreateFilling,
            openConfirmCheckAll,
            closeModal,
            confirmCheckAllData,
        },
    });

    // -----------------------------------------------------------------------
    // Step 11: Tab switching
    // -----------------------------------------------------------------------

    const {topicType, topicName} = useContentParams();

    const isDocGenTabHidden = Object.keys(relatedDocuments).length === 0;

    const effectiveTab = useMemo(() => {
        if (hasOnlyInfoNodes && !isDocGenTabHidden) return "documents";
        if (!hasOnlyInfoNodes && isDocGenTabHidden) return "checklist";
        return dokumentId ? "documents" : "checklist";
    }, [dokumentId, hasOnlyInfoNodes, isDocGenTabHidden]);

    const shouldRenderDocumentView = effectiveTab === "documents";

    // Hide the view switcher when only one view is possible (effectiveTab
    // forces it) or in preview, where topicType/topicName are absent and no
    // valid URLs can be built.
    const showViewTabs = !isDocGenTabHidden && !hasOnlyInfoNodes && !isPreview;

    const checklistBasePath = `/${topicType}/${topicName}/${checklistData.metadata.topicId}`;

    const collectionSuffix = collectionData?.metadata?.collectionId
        ? `/utfylling/${slugify(collectionData.metadata.name ?? "utfylling")}/${collectionData.metadata.collectionId}`
        : "";

    const checklistUrl = `${checklistBasePath}${collectionSuffix}`;
    const documentsUrl = `${checklistBasePath}/dokument/n/i${collectionSuffix}`;

    // -----------------------------------------------------------------------
    // Step 11b: Narrow-viewport state
    // -----------------------------------------------------------------------

    const isMobile = useMediaQuery("(max-width: 991px)");
    const {
        containerRef: sidebarResizeRef,
        size: sidebarSize,
        separatorProps: sidebarSeparatorProps,
    } = useResizable({
        storageKey: "checklist:sidebarWidthPx",
        defaultSize: 400,
        min: 288,
        max: 560,
    });
    const [filterOpen, {toggle: toggleFilter}] = useDisclosure(false);

    // Whether the checklist exposes any selectable global filters — gates the
    // toolbar's settings toggle (mirrors GlobalFilterSettings' own rule).
    const hasGlobalFilters = useMemo(
        () =>
            (checklistData.viewGroups ?? []).filter((g) => g.labels.length > 1)
                .length > 0,
        [checklistData.viewGroups]
    );

    // -----------------------------------------------------------------------
    // Step 12: Context assembly
    // -----------------------------------------------------------------------

    const checklistConfig = useMemo(
        () => ({
            metadata: checklistData.metadata,
            viewGroups: checklistData.viewGroups ?? [],
            labelData,
            defaultCollectionId: checklistData.defaultCollectionId,
        }),
        [
            checklistData.metadata,
            checklistData.viewGroups,
            checklistData.defaultCollectionId,
            labelData,
        ]
    );

    const structureCtx = useMemo<ChecklistStructureValue>(
        () => ({
            collectionData,
            isDraft,
            viewFilteredToc,
            effectiveRootNodeId,
            infoIdSet,
            checklistConfig,
        }),
        [
            collectionData,
            isDraft,
            viewFilteredToc,
            effectiveRootNodeId,
            infoIdSet,
            checklistConfig,
        ]
    );

    const interactionCtx = useMemo<ChecklistInteractionValue>(
        () => ({
            isHidden: (id: string) => hiddenIds.has(id),
            isSearchHit: (id: string) => searchHitIds.has(id),
            getStatus: (id: string): Status | -1 => statusMap[id] ?? -1,
            itemsState: {
                loadingState: state.items,
                elements: state.elements,
                objects: state.objects,
                openItems: state.openItems,
                fetchingAll: state.fetchingAll,
            },
            searchTerm: state.searchTerm,
            observeChecklistItemRef: observeElement,
            unobserveChecklistItemRef: unobserveElement,
            onClickItem: actions.handleClickChecklistItem,
            onClickTocItem: actions.handleClickTocItem,
            onCheckItemStatusClick: actions.handleCheckItemStatusClick,
            topicData,
        }),
        // Functions (observeElement, unobserveElement, handlers) are intentionally
        // omitted: observer functions are stable by design, and handlers are
        // plain closures captured at memo time.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            hiddenIds,
            searchHitIds,
            statusMap,
            state.items,
            state.elements,
            state.objects,
            state.openItems,
            state.fetchingAll,
            state.searchTerm,
            topicData,
        ]
    );

    // -----------------------------------------------------------------------
    // Step 13b: Gate on a dead/inaccessible collection
    // -----------------------------------------------------------------------

    // A collection was requested via the URL but is gone or inaccessible —
    // render a full-view error instead of the (empty/broken) checklist body,
    // which also keeps the comment machinery from mounting. Placed after all
    // hooks to satisfy rules-of-hooks.
    if (collectionId && !isCollectionFetching) {
        // The backend returns 400 for a deleted collection (not 404); present
        // it as "not found".
        if (isCollectionError) {
            return <ResourceError error={{status: 404}} />;
        }
        if (collectionData?.access === false) {
            return (
                <StatusMessage
                    title={t("Phrase.NoAccess")}
                    detail={t("Error.NoFillingAccess")}
                />
            );
        }
    }

    // -----------------------------------------------------------------------
    // Step 14: Layout and rendering
    // -----------------------------------------------------------------------

    return (
        <ChecklistStructureProvider value={structureCtx}>
            <ChecklistInteractionProvider value={interactionCtx}>
                <CommentProvider>
                    {showViewTabs && (
                        <TopBarSlot slot="center">
                            <ChecklistViewTabs
                                activeTab={effectiveTab}
                                checklistUrl={checklistUrl}
                                documentsUrl={documentsUrl}
                            />
                        </TopBarSlot>
                    )}
                    <div
                        className={classes.checklistWrapper}
                        ref={sidebarResizeRef}
                        style={
                            !isMobile
                                ? {
                                      gridTemplateColumns: `${sidebarSize}px 1fr`,
                                  }
                                : undefined
                        }
                    >
                        {!isMobile && (
                            <ResizeHandle
                                {...sidebarSeparatorProps}
                                style={{
                                    position: "absolute",
                                    top: 0,
                                    bottom: 0,
                                    left: `${sidebarSize}px`,
                                    zIndex: 2,
                                }}
                            />
                        )}
                        {isMobile ? (
                            <>
                                <ChecklistMobileToolbar
                                    topicName={checklistConfig.metadata.name}
                                    displayTitle={
                                        collectionData?.metadata?.name ??
                                        t("Info.FillingWithoutName")
                                    }
                                    collectionData={collectionData}
                                    hasGlobalFilters={hasGlobalFilters}
                                    filterOpen={filterOpen}
                                    onToggleFilter={toggleFilter}
                                    setEditFillingOpen={() => openEditFilling()}
                                    setCreateFillingOpen={() =>
                                        openCreateFilling()
                                    }
                                    setOpenFillingOpen={() => openOpenFilling()}
                                    setShareFillingOpen={() =>
                                        openShareFilling()
                                    }
                                    setDuplicateFillingOpen={() =>
                                        openDuplicateFilling()
                                    }
                                    setDeleteFillingOpen={() =>
                                        openDeleteFilling()
                                    }
                                />

                                {hasGlobalFilters && (
                                    <Collapse in={filterOpen}>
                                        <GlobalFilterSettings
                                            className={
                                                classes.mobileFilterPanel
                                            }
                                            viewGroups={
                                                checklistConfig.viewGroups
                                            }
                                            selectedLabels={labelData}
                                            onPatchDimensions={
                                                actions.handlePatchDimensions
                                            }
                                        />
                                    </Collapse>
                                )}

                                {shouldRenderDocumentView ? (
                                    <DocumentSelectorField
                                        collectionData={collectionData}
                                        viewFilteredToc={viewFilteredToc}
                                        effectiveRootNodeId={
                                            effectiveRootNodeId
                                        }
                                        onToggleDocumentSelected={
                                            actions.handleToggleDocumentSelected
                                        }
                                        activeDocumentId={dokumentId}
                                    />
                                ) : (
                                    <ChecklistMobileControls
                                        key={
                                            collectionData?.metadata
                                                ?.collectionId ?? "draft"
                                        }
                                        hasCollection={!!collectionData}
                                        searchTerm={state.searchTerm}
                                        filterState={{
                                            statusFilterSelected:
                                                state.filter
                                                    .statusFilterSelected,
                                            showGroups:
                                                checklistData.showGroups ?? [],
                                            resetKey: state.filter.resetKey,
                                        }}
                                        onSearchChange={
                                            actions.handleSearchChange
                                        }
                                        onStatusToggle={
                                            actions.handleStatusToggle
                                        }
                                    />
                                )}
                            </>
                        ) : (
                            <>
                                <ChecklistConfig
                                    className={classes.checklistConfig}
                                    collectionData={collectionData}
                                    checklistConfig={checklistConfig}
                                    onPatchDimensions={
                                        actions.handlePatchDimensions
                                    }
                                    progress={collectionProgress}
                                    setEditFillingOpen={() => openEditFilling()}
                                    setCreateFillingOpen={() =>
                                        openCreateFilling()
                                    }
                                    setOpenFillingOpen={() => openOpenFilling()}
                                    setShareFillingOpen={() =>
                                        openShareFilling()
                                    }
                                    setDuplicateFillingOpen={() =>
                                        openDuplicateFilling()
                                    }
                                    setDeleteFillingOpen={() =>
                                        openDeleteFilling()
                                    }
                                />

                                {!shouldRenderDocumentView && (
                                    <ChecklistControls
                                        key={
                                            collectionData?.metadata
                                                ?.collectionId ?? "draft"
                                        }
                                        allItemsExpanded={allItemsExpanded}
                                        hasCollection={!!collectionData}
                                        searchTerm={state.searchTerm}
                                        filterState={{
                                            statusFilterSelected:
                                                state.filter
                                                    .statusFilterSelected,
                                            showGroups:
                                                checklistData.showGroups ?? [],
                                            resetKey: state.filter.resetKey,
                                        }}
                                        onExpandAll={actions.handleExpandAll}
                                        onSearchChange={
                                            actions.handleSearchChange
                                        }
                                        onStatusToggle={
                                            actions.handleStatusToggle
                                        }
                                    />
                                )}

                                <div
                                    className={classes.checklistGenerateButtons}
                                >
                                    <GenerateButtons
                                        isChecklist={!shouldRenderDocumentView}
                                        isStandalone={false}
                                        isFetching={isGenerateFetching}
                                        {...generateButtonProps}
                                    />
                                </div>

                                <div
                                    className={classes.checklistTocWrapper}
                                    ref={tocContainerRef}
                                >
                                    {shouldRenderDocumentView ? (
                                        <DocumentsList
                                            collectionData={collectionData}
                                            viewFilteredToc={viewFilteredToc}
                                            effectiveRootNodeId={
                                                effectiveRootNodeId
                                            }
                                            onToggleDocumentSelected={
                                                actions.handleToggleDocumentSelected
                                            }
                                            activeDocumentId={dokumentId}
                                        />
                                    ) : (
                                        <ChecklistToc
                                            nodeId={effectiveRootNodeId}
                                            isRoot
                                        />
                                    )}
                                </div>
                            </>
                        )}

                        {shouldRenderDocumentView ? (
                            <div
                                className={`${classes.checklistItems} ${classes.documentPane ?? ""}`}
                            >
                                <DocgenChecklistView
                                    documentId={
                                        dokumentId ??
                                        DOCUMENT_GENERATOR_PLACEHOLDER_ID
                                    }
                                    collectionId={
                                        collectionData?.metadata?.collectionId
                                    }
                                    isDraft={isDraft}
                                    onPromptNewFilling={openCreateFilling}
                                />
                            </div>
                        ) : (
                            <div
                                className={classes.checklistItems}
                                ref={containerRef}
                            >
                                <div className={classes.checklistDescription}>
                                    <h1>{checklistData.metadata.name}</h1>
                                    {state.elements["xdesc;"] &&
                                        renderNode(
                                            topicData,
                                            "xdesc;",
                                            {},
                                            "xdesc;"
                                        )}
                                </div>
                                <ChecklistTree
                                    activeFilters={activeFilters}
                                    onClearSearchAndFilters={
                                        actions.handleClearSearchAndFilters
                                    }
                                />
                            </div>
                        )}

                        {isMobile && (
                            <div className={classes.mobileActionBar}>
                                <GenerateButtons
                                    isChecklist={!shouldRenderDocumentView}
                                    isStandalone={false}
                                    isFetching={isGenerateFetching}
                                    {...generateButtonProps}
                                />
                            </div>
                        )}
                    </div>

                    <CommentPanel
                        topicId={checklistData.metadata.topicId}
                        collectionId={
                            collectionData?.metadata?.collectionId ?? ""
                        }
                        viewFilteredToc={viewFilteredToc}
                        effectiveRootNodeId={effectiveRootNodeId}
                    />

                    <ChecklistModals
                        collectionData={collectionData}
                        viewFilteredToc={viewFilteredToc}
                        effectiveRootNodeId={effectiveRootNodeId}
                        checklistConfig={checklistConfig}
                        isEditFillingOpen={isEditFillingOpen}
                        isCreateFillingOpen={isCreateFillingOpen}
                        isOpenFillingOpen={isOpenFillingOpen}
                        isShareFillingOpen={isShareFillingOpen}
                        isDuplicateFillingOpen={isDuplicateFillingOpen}
                        isDeleteFillingOpen={isDeleteFillingOpen}
                        confirmCheckAllData={confirmCheckAllData}
                        infoIds={checklistData.infoIds ?? []}
                        leafNodeIds={checklistData.leafNodeIds ?? []}
                        closeModal={closeModal}
                        handleConfirmCheckAll={actions.handleConfirmCheckAll}
                        handlePatchDimensions={actions.handlePatchDimensions}
                        handlePatchCollectionName={
                            actions.handlePatchCollectionName
                        }
                        onCollectionChange={() =>
                            dispatch({
                                type: ChecklistEventTypes.RESET_STATUS,
                            })
                        }
                        isPatchCollectionNameLoading={
                            isPatchCollectionNameLoading
                        }
                        isPatchDimensionsLoading={isPatchDimensionsLoading}
                    />
                </CommentProvider>
            </ChecklistInteractionProvider>
        </ChecklistStructureProvider>
    );
};
